import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { IcArrowRight } from '@healingpaper/cell-icons'
import Link from 'next/link'
import { Typography } from '@healingpaper/cell'

import Text from '@/components/atomic/atoms/Text'
import Section from '@/components/atomic/molecules/Section'
import ReviewCard from '@/components/atomic/organisms/ReviewCard/new'
import ReviewLoginCard from '@/components/atomic/organisms/ReviewLoginCard'
import { useTranslation } from '@/lang'
import api from '@/services/api/v2'
import { useLocales } from '@/shared/hooks/useLocales'

import type { Review, ReviewInternal } from '@/features/review/api/review.type'

const MAX_VISIBLE_REVIEW_COUNT = 3

export interface Props {
  total?: number
  defaultReviews?: Review[]
  reviews?: ReviewInternal[]
  isUser?: boolean
  hospitalId?: number
  eventId?: number
  q?: string
  urlClickMore?: string
  relClickMore?: 'nofollow' | 'follow'
  title?: string
  treatmentId?: number
  categoryId?: number
  onClickMoreReview?: () => void
  onClickReviewCard?: () => void
  onClickLoginButton?: () => void
  renderCustomTitle?: () => React.ReactNode
}

const ReviewTemplate = ({
  total: defaultTotal = 0,
  reviews: injectedReviews,
  defaultReviews,
  isUser = false,
  hospitalId,
  eventId,
  q,
  title,
  treatmentId,
  categoryId,
  urlClickMore,
  relClickMore,
  onClickMoreReview,
  onClickReviewCard,
  onClickLoginButton,
  renderCustomTitle,
}: Props): JSX.Element => {
  const { t } = useTranslation()
  const router = useRouter()
  const [total, setTotal] = useState(defaultTotal)
  const { locale } = useLocales()
  const [reviews, setReviews] = useState<Review[] | null>((injectedReviews as Review[]) || null)

  const renderedTitle = useMemo(() => {
    let target = ''
    if (hospitalId) {
      target = t('review.hospital')
    }
    if (eventId) {
      target = t('review.event')
    }
    if (treatmentId) {
      target = t('review.treatment')
    }
    return (
      <>
        {title || target}
        {total && (
          <>
            <StyledPoint> {total < 10000 ? total.toLocaleString() : '9,999+'}</StyledPoint>
            {t('review.reviewUnit')}
          </>
        )}
      </>
    )
  }, [title, hospitalId, eventId, treatmentId, t, total])

  useEffect(() => {
    const getAuthenticatedReviews = async () => {
      if (!isUser) {
        setReviews(null)
      } else {
        if (defaultReviews) {
          setReviews(defaultReviews.slice(0, MAX_VISIBLE_REVIEW_COUNT))
        } else {
          if (hospitalId) {
            const { data, total } = await api.review.getReviewsInHospital(hospitalId, 0, 3, locale)

            setTotal(total)
            setReviews(data)
          } else if (eventId) {
            const { data, total } = await api.review.getReviewsInEvent(eventId, 0, 3, locale)

            setTotal(total)
            setReviews(data)
          } else if (q || treatmentId || categoryId) {
            const { data, total } = await api.review.getSearchedReviews({ q, treatmentId, categoryId }, 0, 3, locale)

            setTotal(total)
            setReviews(data)
          } else {
            setReviews(null)
          }
        }
      }
    }

    if (injectedReviews === undefined || injectedReviews.length === 0) {
      getAuthenticatedReviews()
    }
  }, [locale, hospitalId, eventId, treatmentId, categoryId, q, defaultReviews, isUser, injectedReviews])

  if (!hospitalId && !eventId && !treatmentId && !categoryId && !q) {
    return <></>
  }

  const handleClickViewReview = () => {
    const query = { return_url: `${location.pathname}${decodeURI(location.search)}` }

    onClickLoginButton?.()

    router.push({ pathname: `/signin`, query })
  }

  const handleClickMore = (e: React.MouseEvent) => {
    onClickMoreReview?.()
  }

  const isReviewVisible = reviews && reviews.length > 0
  const isMoreReviewButtonVisible = urlClickMore && reviews && reviews.length > MAX_VISIBLE_REVIEW_COUNT

  return (
    <Section hasDivider={false} padding={0}>
      <StyledHeading element="h2" type="title3">
        {renderCustomTitle ? renderCustomTitle() : renderedTitle}
      </StyledHeading>
      {reviews === null && (
        <StyledDummyReviewWrapper>
          <ReviewLoginCard
            text={{
              title: t('review.loginCard.title'),
              doLogin: t('review.loginCard.doLogin'),
            }}
            onClickLoginButton={handleClickViewReview}
          />
          <StyledWarning type="description2">{t('review.warning')}</StyledWarning>
        </StyledDummyReviewWrapper>
      )}
      {isReviewVisible && (
        <StyledReviewWrapper>
          {reviews.slice(0, 3).map((review) => (
            <ReviewCard
              key={review.id}
              review={review}
              text={t('review.ReviewCard', { returnObjects: true })}
              onClickReviewCard={onClickReviewCard}
            />
          ))}
        </StyledReviewWrapper>
      )}
      {isMoreReviewButtonVisible && (
        <div className="pt-4">
          <Link
            href={isReviewVisible ? urlClickMore : `/signin`}
            onClick={handleClickMore}
            rel={relClickMore || 'follow'}
          >
            <div
              className="flex flex-row items-center justify-center py-2 px-4 w-full h-[36px] bg-[#ECECEC] rounded-[8px]"
              aria-label="more review button"
            >
              <Typography type="title4" elementType="span">
                {t('review.seeMore')}
              </Typography>
              <IcArrowRight width={16} height={16} />
            </div>
          </Link>
        </div>
      )}
    </Section>
  )
}

const StyledHeading = styled(Text)`
  margin-bottom: 16px;
`
const StyledDummyReviewWrapper = styled.div`
  padding-bottom: unset !important;
`
const StyledPoint = styled.span`
  color: ${({ theme }) => theme.colors.orange};
`
const StyledWarning = styled(Text)`
  display: inline-block;
  color: ${({ theme }) => theme.colors.gray800};
`

const StyledReviewWrapper = styled.div`
  margin-top: 16px;

  > div:last-child {
    div[role='divider'] {
      display: none;
    }
  }
`

export default ReviewTemplate
